// Aligne sur les conventions Supabase JS V2.
// IMPORTANT : ne jamais inliner SUPABASE_SERVICE_ROLE_KEY côté client.
// Toute opération privilégiée passe par une Edge Function.

import { createClient } from "@supabase/supabase-js";
import { createSupabaseSessionRecovery, type SessionAuth } from "../../../shared/supabase-session";

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim();

function isHttpUrl(value: string | undefined): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Configuration absente = console inutilisable, mais **pas** écran blanc.
 *
 * Ce module faisait un `throw` au chargement. Un throw à l'évaluation d'un
 * module casse tout l'arbre d'imports AVANT que React ne monte quoi que ce
 * soit : l'`ErrorBoundary` de l'application n'existe pas encore, et
 * l'exploitant ne voit qu'une page blanche, sans le moindre indice — le pire
 * mode de défaillance possible pour un outil interne.
 *
 * On dégrade donc proprement : la configuration manquante est signalée par
 * `supabaseConfigError`, que `App.tsx` affiche en clair, et le client est créé
 * sur une URL sentinelle qui échouera à la première requête plutôt qu'au
 * chargement. Même doctrine que le portail.
 */
export const supabaseConfigError: string | null =
  !SUPABASE_URL || !SUPABASE_ANON_KEY
    ? "Configuration manquante : VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY doivent être posées AU MOMENT DU BUILD (Vite les fige dans le bundle — un simple redéploiement ne suffit pas). Voir spawt-admin/.env.example."
    : !isHttpUrl(SUPABASE_URL)
      ? "Configuration invalide : VITE_SUPABASE_URL doit être une adresse HTTP ou HTTPS valide. Corrigez la variable puis reconstruisez la console."
      : null;

const clientUrl = supabaseConfigError ? "https://configuration-manquante.invalid" : SUPABASE_URL!;
const recovery = createSupabaseSessionRecovery(clientUrl, () => sessionAuth);

export const supabaseClient = createClient(
  clientUrl,
  supabaseConfigError ? "cle-anon-manquante" : SUPABASE_ANON_KEY!,
  {
    global: { fetch: recovery.fetch },
    auth: {
      persistSession: true,
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      flowType: "pkce",
    },
  },
);
const sessionAuth: SessionAuth = supabaseClient.auth;
export const getAuthenticatedStaff = () => recovery.getUser(() => supabaseClient.auth.getUser());
